import { createApi } from "@reduxjs/toolkit/query/react";
import {
  ReiSiftAccount,
  ReiSiftAccountSearchParams,
  ReiSiftIntegrationFeature,
  ReiSiftPropertyPayload,
  ReiSiftUploadDetails,
  ReiSiftUploadFilePayload,
  VerifyReisiftAccessTokenParams,
  VerifyReiSiftAccessTokenResponse,
} from "@shared/interfaces";
import { showProgress } from "@shared/utils";
import { axiosBaseQuery } from "@shared/utils/request";
import { reiSiftIntegrationApi } from "@shared/api/reiSiftIntegrationApi";
import Methods from "@shared/constants/methods";
import { showNotification } from "@shared/store/actions";

import config from "../../config";

export const reiSiftApi = createApi({
  reducerPath: "reiSiftApi",
  baseQuery: axiosBaseQuery({
    baseUrl: config.baseApiUrl,
  }),
  tagTypes: ["reisift", "search_account", "verify_access_token", "upload_file", "create_property"], // You can add more tags as needed
  endpoints: (build) => ({
    getReiSiftAccountSearch: build.query<ReiSiftAccount, ReiSiftAccountSearchParams>({
      providesTags: ["reisift", "search_account"],
      query: (params) => ({
        url: "reisift/search-account",
        params,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          showProgress(dispatch, queryFulfilled);
        } catch (error) {
          // If an error happens, you can dispatch an action to reset it manually if you want
          dispatch(reiSiftApi.util.updateQueryData("getReiSiftAccountSearch", _, () => undefined));
        }
      },
    }),
    verifyAccessToken: build.query<VerifyReiSiftAccessTokenResponse, VerifyReisiftAccessTokenParams>({
      providesTags: ["reisift", "verify_access_token"],
      query: (params) => ({
        url: "reisift/verify-token",
        params,
      }),
      onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          showProgress(dispatch, queryFulfilled);
          dispatch(
            reiSiftIntegrationApi.endpoints.updateReiSiftIntegration.initiate({
              account_id: params.account_id,
              rei_sift_account_uuid: data?.uuid,
              [ReiSiftIntegrationFeature.PredictiveAi]: true,
              [ReiSiftIntegrationFeature.StackedNiche]: true,
            }),
          ).unwrap();
        } catch (error) {
          // If an error happens, you can dispatch an action to reset it manually if you want
          dispatch(reiSiftApi.util.updateQueryData("verifyAccessToken", params, () => undefined));
        }
      },
    }),
    uploadFileToReiSift: build.mutation<ReiSiftUploadDetails, ReiSiftUploadFilePayload>({
      query: (payload: ReiSiftUploadFilePayload) => ({
        url: "reisift/upload",
        method: Methods.POST,
        body: payload,
      }),
      invalidatesTags: ["reisift", "upload_file"],
      onQueryStarted: (payload, { dispatch, queryFulfilled }) => {
        showProgress(dispatch, queryFulfilled);
        queryFulfilled
          .then(() => {
            dispatch(reiSiftApi.util.resetApiState());

            dispatch(
              showNotification({
                message: `Uploading file ${payload?.file_id} to REISift has started`,
                appearance: "success",
              }),
            );
          })
          .catch(({ error }) => {
            const message =
              typeof error?.data === "string" ? error.data : `Failed to upload file ${payload.file_id} to REISift`;

            dispatch(
              showNotification({
                message,
                appearance: "error",
              }),
            );
          });
      },
    }),
    createReiSiftProperty: build.mutation<void, ReiSiftPropertyPayload>({
      query: (payload: ReiSiftPropertyPayload) => ({
        url: "reisift/property",
        method: Methods.POST,
        body: payload,
      }),
      invalidatesTags: ["reisift", "create_property"],
      onQueryStarted: (payload, { dispatch, queryFulfilled }) => {
        showProgress(dispatch, queryFulfilled);
        queryFulfilled
          .then(() => {
            dispatch(reiSiftApi.util.resetApiState());

            dispatch(
              showNotification({
                message: `Lead ${payload.lead_id} successfully sync to REISift property`,
                appearance: "success",
              }),
            );
          })
          .catch((error) => {
            const message =
              typeof error?.data === "string"
                ? error.data
                : `Failed to sync lead ${payload.lead_id} to REISift property`;

            dispatch(
              showNotification({
                message,
                appearance: "error",
              }),
            );
          });
      },
    }),
  }),
});

export const {
  useLazyGetReiSiftAccountSearchQuery,
  useLazyVerifyAccessTokenQuery,
  useUploadFileToReiSiftMutation,
  useCreateReiSiftPropertyMutation,
} = reiSiftApi;
